import { SuspenseLoader } from '@folio/components/layout/ApplicationLayout/SuspenseLoader';
import {
  AuthRequired,
  ClaimDetailsLayout,
  ClaimDocumentsModule,
  ClaimInformationModule,
  ClaimOverviewModule,
  ClaimPartiesModule,
  FloatingChatModule,
  getAuthRoutes,
  getFederatedAuthRoutes,
  getIntermediaryPageMeta,
  withImplicitIntermediaryPages,
  getPolicyholderPageLabel,
  IntermediaryDetailsLayout,
  LogoModule,
  NotFoundView,
  PolicyDashboardLayout,
  RedirectModule,
  RetailIntermediaryUsersListModule,
  getStopFunnelRoutes,
  TerminationButton,
  WaitingForReviewModule,
  ProductSelectionPage,
} from '@korint/folio';
import {
  CustomFunnelAuth,
  useKorintProductConfig,
  useKorintUniqConfig,
  type IntermediaryPage,
  type IntermediaryPageType,
  type KorintConfig,
  type PolicyDashboardPage,
} from 'korint-sdk/react';
import { Loader2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  type RouteObject,
} from 'react-router-dom';
import { clientOptions } from '../client';
import { DefaultContainerLayout } from '../dashboards/DefaultContainerLayout';
import {
  IntermediaryAccounting,
  IntermediaryAnalytics,
  IntermediaryBrokerageFirmList,
  IntermediaryGeneralInbox,
  IntermediaryClaims,
  IntermediaryDashboardContentLayout,
  IntermediaryArchivedNewBusiness,
  IntermediaryCommissions,
  IntermediaryDerogations,
  IntermediaryDetailsDocuments,
  IntermediaryDetailsPage,
  IntermediaryFraudControl,
  IntermediaryMyBrokerageFirm,
  IntermediaryOnGoingNewBusiness,
  IntermediaryPortfolio,
  IntermediaryProducts,
  IntermediaryReports,
  IntermediaryUserCreation,
  IntermediaryUsersList,
  IntermediaryConversations,
  UniqIntermediaryDashboardLayout,
} from '../dashboards/intermediaryDashboard';
import {
  PolicyCompliance,
  PolicyDocuments,
  PolicyEvents,
  PolicyExchanges,
  PolicyHome,
  PolicyInformation,
  PolicyInvoices,
  PolicyNotifications,
  PolicyPaymentMethods,
} from '../dashboards/policyDashboard';
import {
  PolicyholderDashboardContentLayout,
  PolicyholderDocuments,
  PolicyholderHome,
  PolicyholderInformations,
  PolicyholderInvoices,
  PolicyholderPaymentMethods,
  UniqPolicyholderDashboardLayout,
} from '../dashboards/policyholderDashboard';
import { SuperAdminView } from '../features/SuperAdminView';
import {
  computeCustomFunnelRoutes,
  computeIntermediaryRoutesForStepper,
  computePolicyholderRoutesForStepper,
  computeProductCustomFunnelRoutes,
  computeRoutesForStepper,
} from './routes-for-funnels';
import { CustomFunnelLayout } from './CustomFunnelLayout';
import { EmbeddedAnonymousLayout } from './EmbeddedAnonymousLayout';
import { LegacyDriverRedirect } from './LegacyDriverRedirect';
import { Center } from '@folio/components/ui/flex';

const policyholderDashboardRoutesMapping = {
  home: <PolicyholderHome />,
  information: <PolicyholderInformations />,
  documents: <PolicyholderDocuments />,
  'payment-methods': <PolicyholderPaymentMethods />,
  invoices: <PolicyholderInvoices />,
};

const intermediaryDashboardRoutesMapping = {
  'ongoing-new-business': IntermediaryOnGoingNewBusiness,
  'archived-new-business': IntermediaryArchivedNewBusiness,
  portfolio: IntermediaryPortfolio,
  'intermediary-user-list': IntermediaryUsersList,
  'intermediary-user-form': IntermediaryUserCreation,
  reports: IntermediaryReports,
  commissions: IntermediaryCommissions,
  accounting: IntermediaryAccounting,
  derogations: IntermediaryDerogations,
  'brokerage-firm-list': IntermediaryBrokerageFirmList,
  'my-brokerage-firm': IntermediaryMyBrokerageFirm,
  products: IntermediaryProducts,
  analytics: IntermediaryAnalytics,
  conversations: IntermediaryConversations,
  'general-inbox': IntermediaryGeneralInbox,
  claims: IntermediaryClaims,
  'fraud-control': IntermediaryFraudControl,
} as const satisfies Record<IntermediaryPageType, React.ComponentType>;

const intermediaryDashboardRoutesChildrenMapping: {
  [Key in IntermediaryPage['type']]?: RouteObject[];
} = {
  'brokerage-firm-list': [
    {
      path: ':brokerageFirmId',
      element: <IntermediaryDetailsLayout />,
      children: [
        {
          path: '',
          element: <IntermediaryDetailsPage />,
        },
        {
          path: 'view-documents',
          element: <IntermediaryDetailsDocuments />,
        },
        {
          path: 'users',
          element: <RetailIntermediaryUsersListModule />,
        },
      ],
    },
  ],
  conversations: [
    {
      path: ':conversationId',
      element: <IntermediaryConversations />,
    },
  ],
  claims: [
    {
      path: ':claimId',
      element: <ClaimDetailsLayout />,
      children: [
        {
          path: '',
          element: <ClaimOverviewModule />,
        },
        {
          path: 'information',
          element: <ClaimInformationModule />,
        },
        {
          path: 'parties',
          element: <ClaimPartiesModule />,
        },
        {
          path: 'documents',
          element: <ClaimDocumentsModule />,
        },
      ],
    },
  ],
};

const policyDashboardRoutesMapping = {
  home: PolicyHome,
  informations: PolicyInformation,
  'payment-methods': PolicyPaymentMethods,
  documents: PolicyDocuments,
  invoices: PolicyInvoices,
  events: PolicyEvents,
  notifications: PolicyNotifications,
  compliance: PolicyCompliance,
  exchanges: PolicyExchanges,
};

function renderIntermediaryComponent(page: IntermediaryPage) {
  if (page.type === 'custom') {
    return page.component;
  }

  const Component = intermediaryDashboardRoutesMapping[page.type];

  return <Component />;
}

function renderPolicyComponent(page: PolicyDashboardPage) {
  if (typeof page !== 'string') {
    return page.component;
  }

  const Component = policyDashboardRoutesMapping[page];
  return <Component />;
}

function createRoutesFromConfig(
  config: NonNullable<KorintConfig['uniqAppConfig']>,
  productConfig: NonNullable<KorintConfig['productsConfig']>,
  t: (key: string) => string,
) {
  const intermediaryPages = withImplicitIntermediaryPages(config.intermediaryDashboard.pages);
  const policyPages: ReadonlyArray<PolicyDashboardPage> = config.policyDashboard.pages.includes(
    'exchanges',
  )
    ? config.policyDashboard.pages
    : [...config.policyDashboard.pages, 'exchanges'];

  const globalRoutes = {
    element: (
      <AuthRequired>
        <div className="container mx-auto p-4">
          <Outlet />
        </div>
      </AuthRequired>
    ),
    children: [
      {
        path: '/',
        // @debt : LogoModule should be used directly in the RedirectModule
        element: <RedirectModule logo={<LogoModule className="h-8 w-auto" />} />,
      },
      {
        path: '/redirect',
        // @debt : LogoModule should be used directly in the RedirectModule
        element: <RedirectModule logo={<LogoModule className="h-8 w-auto" />} />,
      },
      {
        path: '/driver/*',
        element: (
          <SuspenseLoader>
            <LegacyDriverRedirect productsConfig={productConfig} />
          </SuspenseLoader>
        ),
      },
      {
        path: '/admin',
        element: <SuperAdminView />,
      },
      {
        path: '/waiting',
        element: (
          <WaitingForReviewModule
            title="Votre demande est en cours de revue"
            message="Merci pour votre inscription. Votre demande sera examinée dans les plus brefs délais."
          />
        ),
      },
    ],
  };

  const policyholderFunnelRoutes = {
    element: (
      <AuthRequired>
        <SuspenseLoader>
          <DefaultContainerLayout />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: [
      ...Object.entries(productConfig).flatMap(([productId, config]) => {
        return computePolicyholderRoutesForStepper(productId, config);
      }),
      ...computeIntermediaryRoutesForStepper(config),
      ...getStopFunnelRoutes({
        basePath: '/policyholder/:policyId/stop',
        afterEndPath: '/policyholder/:policyId',
      }),
    ],
  };

  const customFunnelRoutesData = computeCustomFunnelRoutes(config);
  const productCustomFunnelRoutesData = computeProductCustomFunnelRoutes(productConfig);
  const allCustomFunnelRoutesData = [...customFunnelRoutesData, ...productCustomFunnelRoutesData];

  const customFunnelRoutes = allCustomFunnelRoutesData.flatMap(
    ({ routes, layout, auth, customLayout }) => {
      switch (layout) {
        case 'policy':
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <UniqIntermediaryDashboardLayout pages={intermediaryPages} />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes.map(route => ({
              ...route,
              element: (
                <PolicyDashboardLayout pages={policyPages} stopFunnelBasePath="/policies">
                  {route.element}
                </PolicyDashboardLayout>
              ),
            })),
          };

        case 'intermediary':
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <UniqIntermediaryDashboardLayout pages={intermediaryPages} />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes,
          };

        case 'policyholder':
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <UniqPolicyholderDashboardLayout pages={config.policyholderDashboard.pages} />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes,
          };

        case 'embedded':
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <EmbeddedAnonymousLayout />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes,
          };

        case 'custom':
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <CustomFunnelLayout customLayout={customLayout} />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes,
          };

        case 'none':
        default:
          return {
            element: (
              <AuthRequired auth={auth}>
                <SuspenseLoader>
                  <DefaultContainerLayout />
                </SuspenseLoader>
              </AuthRequired>
            ),
            children: routes,
          };
      }
    },
  );

  const anonymousPageRoutes = Object.values(productConfig).flatMap(
    ({ anonymousPages = [] }) => anonymousPages,
  );
  const anonymousPagesRoute = {
    element: (
      <AuthRequired auth={CustomFunnelAuth.ANONYMOUS}>
        <SuspenseLoader>
          <DefaultContainerLayout />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: anonymousPageRoutes,
  };

  const policyholderDashboardRoutes = {
    element: (
      <AuthRequired>
        <SuspenseLoader>
          <UniqPolicyholderDashboardLayout pages={config.policyholderDashboard.pages} />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: config.policyholderDashboard.pages.map(page => ({
      path: `policyholder/:policyId/:branchId?${page.type === 'custom' ? page.path : page.type === 'home' ? '' : `/${page.type}`}`,
      element: (
        <PolicyholderDashboardContentLayout
          title={getPolicyholderPageLabel(page, t)}
          right={
            page.type === 'home' && config.policyholderDashboard.showTerminationButton ? (
              <TerminationButton funnelBasePath="/policyholder" />
            ) : undefined
          }
        >
          {page.type === 'custom' ? page.component : policyholderDashboardRoutesMapping[page.type]}
        </PolicyholderDashboardContentLayout>
      ),
    })),
  };

  const intermediaryFunnelRoutes = Object.entries(productConfig).map(([productId, config]) => {
    return computeRoutesForStepper(productId, config);
  });

  const intermediaryDashboardRoutes = {
    element: (
      <AuthRequired>
        <SuspenseLoader>
          <UniqIntermediaryDashboardLayout pages={intermediaryPages} />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: [
      ...[...intermediaryPages, ...config.intermediaryDashboard.otherPages].map(page => {
        const intermediaryPage = page as IntermediaryPage;
        const hasTitleFromChildren = Boolean(
          intermediaryDashboardRoutesChildrenMapping[intermediaryPage.type],
        );

        return {
          // Must match the sidebar link, which addresses custom pages by `path`, not by type.
          path: intermediaryPage.type === 'custom' ? intermediaryPage.path : intermediaryPage.type,
          element: (
            <IntermediaryDashboardContentLayout
              type={intermediaryPage.type}
              title={
                hasTitleFromChildren ? false : getIntermediaryPageMeta(intermediaryPage, t).label
              }
            >
              <Outlet />
            </IntermediaryDashboardContentLayout>
          ),
          children: [
            {
              path: '',
              element: renderIntermediaryComponent(intermediaryPage),
            },
            ...(intermediaryDashboardRoutesChildrenMapping[intermediaryPage.type] ?? []),
          ],
        };
      }),
      ...intermediaryFunnelRoutes.flat(),
      ...getStopFunnelRoutes({
        basePath: '/policies/:policyId/stop',
        afterEndPath: '/policies/:policyId',
      }),
      {
        path: '/new-business',
        element: <ProductSelectionPage />,
      },
    ],
  };

  const policyDashboardRoutes = {
    element: (
      <AuthRequired>
        <SuspenseLoader>
          <UniqIntermediaryDashboardLayout pages={intermediaryPages} />
        </SuspenseLoader>
      </AuthRequired>
    ),
    children: [
      ...policyPages.map(page => {
        const pathSuffix =
          typeof page === 'string' ? (page === 'home' ? '' : `/${page}`) : page.path;
        return {
          path: `policies/:policyId/:branchId?${pathSuffix}`,
          element: (
            <PolicyDashboardLayout pages={policyPages} stopFunnelBasePath="/policies">
              {renderPolicyComponent(page)}
            </PolicyDashboardLayout>
          ),
        };
      }),
    ],
  };

  const providers = config.login?.providers ?? [];
  const showLastLoginHint = config.login?.showLastLoginHint ?? false;

  const unauthenticatedRoutes = [
    ...(clientOptions.federatedLogin
      ? getFederatedAuthRoutes({ logo: <LogoModule /> })
      : getAuthRoutes({
          logo: <LogoModule />,
          providers,
          showLastLoginHint,
          link: config.login?.link,
        })),
    { path: '*', element: <NotFoundView /> },
  ];

  const authenticatedRoutes = [
    globalRoutes,
    policyholderFunnelRoutes,
    policyholderDashboardRoutes,
    intermediaryDashboardRoutes,
    policyDashboardRoutes,
    ...customFunnelRoutes,
    anonymousPagesRoute,
  ];

  return [
    {
      element: (
        <>
          <ScrollRestoration />
          <Outlet />
          <FloatingChatModule />
        </>
      ),
      children: [...unauthenticatedRoutes, ...authenticatedRoutes],
    },
  ] satisfies RouteObject[];
}

export function UniqAppRouter() {
  const config = useKorintUniqConfig();
  const { productsConfig, isLoading } = useKorintProductConfig();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Center className="py-32">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );
  }

  const routes = createRoutesFromConfig(config, productsConfig, t);

  return <RouterProvider router={createBrowserRouter(routes)} />;
}
